var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { gql, useQuery } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useCurrentTripId } from '../search/currentTripState';
import { callSideEffects } from '../../util';
import { DateFormats, CurrencyFormats } from '../../contracts/types';
import { currentAvailableFlightFiltersVar, currentFlightFilterSelectionsVar } from '../filter/currentFlightFilters';
import { currentFlightSortCriterionVar } from '../sort';
import { FlightLeg } from '../../util/flight/flightLeg';
export const FLIGHTS_PAGE_SIZE = 15;
const INITIAL_MAX_FLIGHTS_SIZE = 16;
const FLIGHT_PAGE_INDEX = 0;
export function useFlightSearchResults(sideEffects, hotelCheckInDate = '', optionalFields = '', skip = false, tripId = null, estabId = null) {
    var _a;
    const FLIGHTS_SEARCH_RESULTS_QUERY = gql `
        query FlightSearchResults(
            $tripStateId: ID!
            $paging: Paging!
            $departureDateFormat: DateFormats
            $timeFormat: DateFormats
            $currencyFormat: CurrencyFormats
            $filters: FlightFiltersInput
            $sortCriterion: FlightSortCriterion
            $hotelCheckInDate: String
            $estabId: Int
        ) {
            flightSearchResults(tripStateId: $tripStateId,filters: $filters,sortCriterion: $sortCriterion, paging: $paging, hotelCheckInDate: $hotelCheckInDate, estabId: $estabId) {
                totalFlights
                filteredFlights
                flights {
                    flightId
                    outboundLeg {
                    ${FlightLeg}
                  }
                  inboundLeg {
                    ${FlightLeg}
                  }
                    checkInAdjustment {
                        dayDifferenceToDeparture
                        dayDifferenceHotelSearchId
                        dayDifferenceHotelNotAvailable
                        dayDifferenceHotelProductId
                        dayDifferenceHotelCheckIn
                        dayDifferenceHotelNights
                    }
                    isOpenJaw
                    isPreSelected
                    deltaPrice
                    deltaPriceFormatted(format: $currencyFormat)
                    pricePerPerson
                    pricePerPersonFormatted(format: $currencyFormat)
                    totalPrice
                    totalPriceFormatted(format: $currencyFormat)
                    pricePerAdult
                    pricePerAdultFormatted(format: $currencyFormat)
                    deltaTeaserPrice(rounded: true) {
                        parts {
                            type
                            value
                        }
                        value
                        formatted
                    }
                    teaserPricePerAdult(rounded: true) {
                        parts {
                            type
                            value
                        }
                        value
                        formatted
                    }
                }
                availableFilters {
                    ... on CheckboxFilterGroup {
                        id
                        name
                        type
                        pill
                        items {
                            id
                            label
                            count
                            selected
                        }
                        open
                    }
                    ... on RangeFilterGroup {
                        id
                        name
                        type
                        min
                        max
                        step
                        formatString
                        lower
                        upper
                        open
                        leadingText
                    }
                }
                sortCriterion
                ${optionalFields}
            }
        }
    `;
    // Needed due to broken contract between graphql-client
    const [lastPageIndex, setLastPageIndex] = useState(0);
    const [tripIdState] = useCurrentTripId();
    const tripStateId = tripId !== null && tripId !== void 0 ? tripId : (_a = tripIdState.data) === null || _a === void 0 ? void 0 : _a.currentTripStateId;
    const _b = useQuery(FLIGHTS_SEARCH_RESULTS_QUERY, {
        notifyOnNetworkStatusChange: true,
        ssr: false,
        skip: skip || !tripStateId,
        variables: {
            paging: {
                pageIndex: FLIGHT_PAGE_INDEX,
                pageSize: FLIGHTS_PAGE_SIZE,
            },
            tripStateId: tripStateId !== null && tripStateId !== void 0 ? tripStateId : '',
            departureDateFormat: DateFormats.Fulldate,
            timeFormat: DateFormats.Time,
            currencyFormat: CurrencyFormats.Rounded,
            filters: currentFlightFilterSelectionsVar(),
            sortCriterion: currentFlightSortCriterionVar(),
            hotelCheckInDate,
            estabId,
        },
        fetchPolicy: 'cache-and-network',
        onCompleted: (completedData) => {
            var _a, _b, _c, _d;
            if ((_a = completedData === null || completedData === void 0 ? void 0 : completedData.flightSearchResults) === null || _a === void 0 ? void 0 : _a.availableFilters) {
                currentAvailableFlightFiltersVar(completedData.flightSearchResults.availableFilters);
            }
            if ((_b = completedData === null || completedData === void 0 ? void 0 : completedData.flightSearchResults) === null || _b === void 0 ? void 0 : _b.sortCriterion) {
                currentFlightSortCriterionVar(completedData.flightSearchResults.sortCriterion);
            }
            // Needed due to broken contract between graphql-client
            const flightArrLength = (_d = (_c = completedData.flightSearchResults) === null || _c === void 0 ? void 0 : _c.flights) === null || _d === void 0 ? void 0 : _d.length;
            if (!flightArrLength ||
                lastPageIndex === 0 ||
                (lastPageIndex !== 0 && flightArrLength <= INITIAL_MAX_FLIGHTS_SIZE)) {
                setLastPageIndex(1);
            }
            if (sideEffects) {
                callSideEffects(sideEffects, completedData);
            }
        },
    }), { data, fetchMore } = _b, others = __rest(_b, ["data", "fetchMore"]);
    const fetchNext = useCallback(() => {
        fetchMore({
            variables: {
                paging: {
                    pageIndex: FLIGHT_PAGE_INDEX + lastPageIndex,
                    pageSize: FLIGHTS_PAGE_SIZE,
                },
                tripStateId,
                filters: currentFlightFilterSelectionsVar(),
                sort: currentFlightSortCriterionVar(),
            },
        });
        setLastPageIndex(lastPageIndex + 1);
    }, [fetchMore, lastPageIndex, tripStateId]);
    return [Object.assign({ data, fetchMore }, others), fetchNext];
}
